import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { HStack } from '~/components/elements/HStack'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCustomerAttributeGroup } from '../hooks/useCustomerAttributeGroup'
import { useCustomerAttributeGroupId } from '../hooks/useCustomerAttributeGroupId'
import { useSortCustomerAttributesMutation } from '../hooks/useSortCustomerAttributesMutation'

import styles from './SortCustomerAttributeDialog.css'

export const SortCustomerAttributeDialog = () => {
  const customerAttributeGroup = useCustomerAttributeGroup()
  const customerAttributes = React.useMemo(
    () => customerAttributeGroup?.customerAttributes ?? [],
    [customerAttributeGroup?.customerAttributes],
  )

  const [sortedAttributes, setSortedAttributes] =
    React.useState(customerAttributes)

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const sortCustomerAttributesMutation = useSortCustomerAttributesMutation({
    onSuccess: (data) => {
      // 対象グループが見つからない場合などupdateCustomerAttributeOrderIndexesはresult: falseを返す。並び替えできていないので記録しない。
      if (data.updateCustomerAttributeOrderIndexes?.result) {
        logEvent('Sort customer attribute')
      }
      closeDialogWithRefresh()
    },
  })

  const handleMoveUp = React.useCallback<
    (index: number) => EH<StyledButtonProps, 'onClick'>
  >(
    (index) => () => {
      setSortedAttributes(R.swapIndices(index - 1, index))
    },
    [],
  )

  const handleMoveDown = React.useCallback<
    (index: number) => EH<StyledButtonProps, 'onClick'>
  >(
    (index) => () => {
      setSortedAttributes(R.swapIndices(index, index + 1))
    },
    [],
  )

  const customerAttributeGroupId = useCustomerAttributeGroupId()
  const handleExecute = React.useCallback(() => {
    sortCustomerAttributesMutation.mutate({
      customerAttributeGroupId,
      input: sortedAttributes.map((attribute, index) => ({
        id: attribute.id,
        orderIndex: index,
      })),
    })
  }, [
    sortCustomerAttributesMutation,
    customerAttributeGroupId,
    sortedAttributes,
  ])

  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'sort') {
    throw new Error()
  }

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const { t } = useTranslation()

  return (
    <ExecuteCancelDialog
      disabledExecute={false}
      executing={sortCustomerAttributesMutation.isPending}
      mode="settings"
      title={t('sort_customized_segments')}
      onCancel={resetDialogInfo}
      onExecute={handleExecute}
    >
      <VStack spacing={2}>
        {sortedAttributes.map((attribute, index) => (
          <HStack key={attribute.id} align="center" spacing={2}>
            <div className={styles.itemName}>{attribute.name}</div>

            <StyledButton
              buttonStyle="normal"
              disabled={index === 0}
              icon="arrow-up"
              onClick={handleMoveUp(index)}
            >
              {t('sort_up')}
            </StyledButton>

            <StyledButton
              buttonStyle="normal"
              disabled={index === sortedAttributes.length - 1}
              icon="arrow-down"
              onClick={handleMoveDown(index)}
            >
              {t('sort_down')}
            </StyledButton>
          </HStack>
        ))}
      </VStack>
    </ExecuteCancelDialog>
  )
}
